import { useEffect, useRef, useState } from 'react';

interface Depoimento {
  id: string;
  emoji: string;
  badge: string;
  text: string;
  footer: string;
  selo?: string;
}

const depoimentos: Depoimento[] = [
  {
    id: 'dep1',
    emoji: '❤️',
    badge: 'Paciente Verificada',
    text: '"Não tenho como não agradecer imensamente por tudo o que você fez por mim durante todo esse ano. Nunca imaginei que pudesse alcançar o que alcancei sem cirurgia. Nunca imaginei que pudesse existir uma médica como você. Você cuidou de mim, não soltou meu braço nem nos momentos mais difíceis. Nossa relação nunca se resumiu a uma consulta. Te admiro muito como médica, mas ainda mais como pessoa. Obrigada por cuidar de mim e me fazer enxergar quem eu sou e quem eu quero ser."',
    footer: 'Paciente • Fortaleza/CE',
  },
  {
    id: 'dep2',
    emoji: '✨',
    badge: 'Paciente Verificada',
    text: '"Dra, aquela injeção é tudoooo! Pense num resultado imediato. Eu amei de verdade. Melhorou muita coisa em mim. Até meu intestino está funcionando melhor. E ela seca mesmo, viu!"',
    footer: 'Paciente • Fortaleza/CE',
    selo: 'Resultado compartilhado espontaneamente',
  },
  {
    id: 'dep3',
    emoji: '💪',
    badge: 'Paciente Verificada',
    text: '"Depois de um ano me recuperando de uma cirurgia, consegui voltar para a academia. Quando a série era de 15 repetições e eu chegava na décima primeira, lembrava de você e da lição que me ensinou: Precisamos vencer a nós mesmos. Saí da academia sem as pernas responderem, mas com algo muito maior: Disciplina e foco."',
    footer: 'Paciente • Fortaleza/CE',
  },
  {
    id: 'dep4',
    emoji: '🌿',
    badge: 'Paciente Verificada',
    text: '"Obrigada, Mel. Você tem sido essencial em todo o meu processo. Todos os benefícios que vêm acontecendo me fazem muito feliz. Obrigada por fazer parte dessa transformação."',
    footer: 'Paciente • Fortaleza/CE',
  },
];

function DepoimentoCard({ depoimento, index }: { depoimento: Depoimento; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.15 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  const isLeft = index % 2 === 0;

  return (
    <div
      ref={cardRef}
      className="relative break-inside-avoid mb-4 md:mb-6"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView
          ? 'translateY(0) translateX(0)'
          : `translateY(30px) translateX(${isLeft ? '-20px' : '20px'})`,
        transition: `all 0.8s ease-out ${index * 140}ms`,
      }}
    >
      <div
        className="relative rounded-[24px] p-6 md:p-8 transition-all duration-500 overflow-hidden"
        style={{
          transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
          backgroundColor: hovered ? 'rgba(248, 247, 245, 0.04)' : 'rgba(248, 247, 245, 0.02)',
          border: hovered
            ? '1px solid rgba(200, 169, 106, 0.15)'
            : '1px solid rgba(248, 247, 245, 0.06)',
        }}
      >
        <div
          className="absolute left-0 top-[10%] h-[80%] w-[3px] rounded-r-full transition-all duration-500"
          style={{
            opacity: hovered ? 1 : 0,
            backgroundColor: '#C8A96A',
            boxShadow: '0 0 12px rgba(200, 169, 106, 0.3)',
          }}
        />

        <div
          className="absolute top-4 right-6 text-6xl leading-none select-none"
          style={{
            fontFamily: 'var(--font-heading)',
            color: 'rgba(200, 169, 106, 0.08)',
          }}
        >
          &ldquo;
        </div>

        <div className="flex items-center gap-2 mb-4">
          <span className="text-lg">{depoimento.emoji}</span>
          <span
            className="text-[11px] tracking-[0.04em]"
            style={{
              fontFamily: 'var(--font-body)',
              color: '#C8A96A',
              fontWeight: 500,
            }}
          >
            {depoimento.badge}
          </span>
        </div>

        <p
          className="text-sm md:text-base leading-relaxed mb-5 relative z-10 italic"
          style={{
            fontFamily: 'var(--font-body)',
            color: 'rgba(248, 247, 245, 0.65)',
            fontWeight: 300,
            lineHeight: 1.9,
          }}
        >
          {depoimento.text}
        </p>

        <div
          className="text-xs tracking-[0.04em]"
          style={{
            fontFamily: 'var(--font-body)',
            color: 'rgba(248, 247, 245, 0.3)',
            fontWeight: 400,
          }}
        >
          {depoimento.footer}
        </div>

        {depoimento.selo && (
          <div className="mt-3 flex items-center gap-1.5">
            <i
              className="ri-verified-badge-line text-sm"
              style={{ color: '#C8A96A' }}
            />
            <span
              className="text-[10px] tracking-[0.04em]"
              style={{
                fontFamily: 'var(--font-body)',
                color: '#C8A96A',
                fontWeight: 400,
              }}
            >
              {depoimento.selo}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Depoimentos() {
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="depoimentos" className="relative py-20 md:py-28 overflow-hidden" style={{ backgroundColor: '#0E1412' }}>
      <div
        className="absolute top-0 left-0 right-0 h-[1px]"
        style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(200, 169, 106, 0.15) 50%, transparent 100%)' }}
      />

      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="text-center mb-16 md:mb-20">
          <p
            className="text-sm tracking-[0.15em] mb-4"
            style={{
              fontFamily: 'var(--font-body)',
              color: '#C8A96A',
              fontWeight: 500,
            }}
          >
            DEPOIMENTOS
          </p>
          <h2
            className="text-3xl md:text-5xl mb-5"
            style={{
              fontFamily: 'var(--font-heading)',
              color: '#F8F7F5',
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            Histórias Reais
          </h2>
          <p
            className="text-sm md:text-base max-w-[560px] mx-auto"
            style={{
              fontFamily: 'var(--font-body)',
              color: 'rgba(248, 247, 245, 0.4)',
              fontWeight: 300,
              lineHeight: 1.7,
            }}
          >
            Cada mensagem representa uma transformação vivida por uma paciente. Mais do que números na balança, são histórias de saúde, autoestima e qualidade de vida recuperadas.
          </p>
        </div>

        <div className="columns-1 md:columns-2 gap-4 md:gap-6 space-y-4 md:space-y-6 max-w-[1000px] mx-auto">
          {depoimentos.map((dep, index) => (
            <DepoimentoCard key={dep.id} depoimento={dep} index={index} />
          ))}
        </div>

        {/* Call to action */}
        <div
          className="text-center mt-16 md:mt-24"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out 0.6s',
          }}
        >
          <h3
            className="text-2xl md:text-4xl mb-4"
            style={{
              fontFamily: 'var(--font-heading)',
              color: '#F8F7F5',
              fontWeight: 500,
            }}
          >
            A próxima história pode ser a sua.
          </h3>
          <p
            className="text-sm md:text-base mb-8"
            style={{
              fontFamily: 'var(--font-body)',
              color: 'rgba(248, 247, 245, 0.4)',
              fontWeight: 300,
            }}
          >
            Cada paciente possui uma jornada única. O primeiro passo para transformar sua saúde começa com uma avaliação individualizada.
          </p>
          <a
            href="https://wa.me/5585999372109?utm_source=site&utm_medium=depoimentos&utm_content=agendar_consulta"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-3.5 rounded-full text-sm font-semibold tracking-[0.03em] whitespace-nowrap cursor-pointer transition-all duration-300"
            style={{
              backgroundColor: 'rgba(200, 169, 106, 0.9)',
              color: '#0E1412',
              fontFamily: 'var(--font-body)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(200, 169, 106, 1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(200, 169, 106, 0.9)';
            }}
          >
            Agendar minha consulta
          </a>
        </div>
      </div>
    </section>
  );
}